import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useResourceItem } from '#client/hooks/useResourceItem';
import { Button } from './Button';
import { Page } from './Page';
import { PageLoading, PageMessage } from './PageStatus';

interface FormContext<T> {
  /** Undefined on the create route. Its presence is what "editing" means. */
  id?: string | undefined;
  /** The loaded record when editing; undefined when creating. */
  record?: T | undefined;
}

interface ResourceFormShellProps<T> {
  /** API path and route base, e.g. `/panels`. Also where Back and Cancel return. */
  path: string;
  /** Singular lowercase noun: "panel" gives "Add panel" / "Edit panel". */
  noun: string;
  fullWidth?: boolean | undefined;
  /** Overrides the derived title, for a screen whose heading is not "Add x". */
  title?: string | undefined;
  /**
   * Rendered only once the record is available, or immediately when creating,
   * so the form's `useState` can initialise from it directly.
   *
   * This is what avoids copying props into state inside an effect — the form
   * component simply mounts with the right values.
   */
  children: (context: FormContext<T>) => ReactNode;
}

/**
 * The chrome around every create and edit form: route params, the fetch when
 * editing, the heading, Back, and the load and error states.
 *
 * Five form pages held a character-identical seventeen-line copy of this
 * wrapper. Their `loading={isEdit && loading}` and `error={isEdit ? error :
 * undefined}` guards were redundant — `useResourceItem` already returns exactly
 * that, because an undefined id means no request.
 *
 * The inner form components stay exported and routing-free, taking `{ id,
 * record }`. That is deliberate: this shell reads `useParams`, so a form
 * mounted outside the router — a quick-add dialog, say — bypasses the shell and
 * renders the form directly.
 */
export function ResourceFormShell<T>({
  path,
  noun,
  fullWidth = false,
  title,
  children,
}: ResourceFormShellProps<T>) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useResourceItem<T>(path, id);

  const heading = title ?? `${id === undefined ? 'Add' : 'Edit'} ${noun}`;
  const back = (
    <Button icon={<ArrowLeft />} aria-label="Back" onClick={() => void navigate(path)} />
  );

  if (loading) return <PageLoading title={heading} backAction={back} fullWidth={fullWidth} />;

  if (error !== undefined) {
    return (
      <PageMessage title={heading} backAction={back} fullWidth={fullWidth}>
        {error}
      </PageMessage>
    );
  }

  return (
    <Page title={heading} backAction={back} fullWidth={fullWidth}>
      {children({ id, record: data })}
    </Page>
  );
}
